import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isNative } from "@/lib/native";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/track`;
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function visitorId() {
  let v = localStorage.getItem("astra_vid");
  if (!v) localStorage.setItem("astra_vid", (v = uid()));
  return v;
}

// A visit (session) ends after 30 minutes without activity.
function sessionId() {
  const now = Date.now();
  let s = JSON.parse(sessionStorage.getItem("astra_sid") || "null");
  if (!s || now - s.at > 30 * 60 * 1000) s = { id: uid(), at: now };
  s.at = now;
  sessionStorage.setItem("astra_sid", JSON.stringify(s));
  return s.id;
}

function send(body, beacon = false) {
  const data = JSON.stringify(body);
  if (beacon && navigator.sendBeacon) navigator.sendBeacon(API, new Blob([data], { type: "application/json" }));
  else {
    const t = localStorage.getItem("astra_token");
    const headers = { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
    fetch(API, { method: "POST", headers, body: data, credentials: "include", keepalive: true }).catch(() => {});
  }
}

function country() {
  const c = localStorage.getItem("astra_country");
  if (c) return c;
  const m = (navigator.language || "").match(/-([A-Z]{2})$/i);
  return m ? m[1].toUpperCase() : "??";
}

// Sends GA page_view on SPA route changes, plus Astra's own anonymous visit stats for /analytics.
export default function RouteTracker() {
  const location = useLocation();
  const first = useRef(true);
  const view = useRef(null);
  const last = useRef({ key: null, at: 0 });

  useEffect(() => {
    const leave = (beacon) => {
      const v = view.current;
      if (!v) return;
      const ms = v.ms + (v.since ? Date.now() - v.since : 0);
      send({ event: "leave", id: v.id, duration_ms: Math.round(ms) }, beacon);
    };
    const onVis = () => {
      const v = view.current;
      if (!v) return;
      if (document.visibilityState === "hidden") {
        if (v.since) v.ms += Date.now() - v.since;
        v.since = null;
        leave(true);
      } else {
        v.since = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", () => leave(true));
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const key = location.pathname + location.search;
    if (last.current.key === key && Date.now() - last.current.at < 1500) return;
    last.current = { key, at: Date.now() };
    const prev = view.current;
    if (prev) send({ event: "leave", id: prev.id, duration_ms: Math.round(prev.ms + (prev.since ? Date.now() - prev.since : 0)) });
    if (location.pathname.startsWith("/analytics")) {
      view.current = null;
    } else {
      const id = uid();
      const params = new URLSearchParams(location.search);
      view.current = { id, ms: 0, since: Date.now() };
      send({
        event: "view", id, vid: visitorId(), sid: sessionId(), path: location.pathname,
        referrer: first.current ? document.referrer : "", utm_source: params.get("utm_source") || (params.get("ref") ? "invite link" : null),
        country: country(), native: isNative,
      });
    }

    if (first.current) {
      first.current = false;
      return;
    }
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
}
